import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ApiMessageResponse } from "@core/types/apiMessageResponse";
import type { OriginScope } from "@features/report/template/types/reportTemplate";
import type {
  PresentationLayoutDetail,
  PresentationLayoutMeta,
  PresentationLayoutQueryParams,
  PresentationLayoutQueryResponse,
  UpsertPresentationLayoutPayload,
} from "@features/presentationLayout/types/presentationLayout";

const basePath = (nationId: number, departmentId: number) =>
  `/nation/${nationId}/department/${departmentId}/presentation-layout`;

function toQueryString(params: PresentationLayoutQueryParams): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function queryPresentationLayouts(
  nationId: number,
  departmentId: number,
  params: PresentationLayoutQueryParams = { all: true }
): Promise<ApiResponse<PresentationLayoutQueryResponse>> {
  return sendRequest<unknown, PresentationLayoutQueryResponse>(
    "GET",
    `${basePath(nationId, departmentId)}/query${toQueryString(params)}`
  );
}

export function fetchPresentationLayoutMeta(
  nationId: number,
  departmentId: number
): Promise<ApiResponse<PresentationLayoutMeta>> {
  return sendRequest<unknown, PresentationLayoutMeta>("GET", `${basePath(nationId, departmentId)}/meta`);
}

export function fetchPresentationLayout(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<PresentationLayoutDetail>> {
  return sendRequest<unknown, PresentationLayoutDetail>("GET", `${basePath(nationId, departmentId)}/${id}`);
}

export function createPresentationLayout(
  nationId: number,
  departmentId: number,
  payload: UpsertPresentationLayoutPayload
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<UpsertPresentationLayoutPayload, ApiMessageResponse>(
    "POST",
    `${basePath(nationId, departmentId)}/create`,
    payload
  );
}

export function updatePresentationLayout(
  nationId: number,
  departmentId: number,
  id: number,
  payload: UpsertPresentationLayoutPayload
): Promise<ApiResponse<ApiMessageResponse>> {
  const { origin_scope: _omit, ...body } = payload;
  return sendRequest<Omit<UpsertPresentationLayoutPayload, "origin_scope">, ApiMessageResponse>(
    "POST",
    `${basePath(nationId, departmentId)}/${id}/update`,
    body
  );
}

export function changePresentationLayoutOrigin(
  nationId: number,
  departmentId: number,
  id: number,
  new_origin_scope: OriginScope
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<{ new_origin_scope: OriginScope }, ApiMessageResponse>(
    "POST",
    `${basePath(nationId, departmentId)}/${id}/changeorigin`,
    { new_origin_scope }
  );
}

export function enablePresentationLayout(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<unknown, ApiMessageResponse>("POST", `${basePath(nationId, departmentId)}/${id}/enable`);
}

export function disablePresentationLayout(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<unknown, ApiMessageResponse>("POST", `${basePath(nationId, departmentId)}/${id}/disable`);
}

export function deletePresentationLayout(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<unknown, ApiMessageResponse>("POST", `${basePath(nationId, departmentId)}/${id}/delete`);
}

export function reorderPresentationLayouts(
  nationId: number,
  departmentId: number,
  presentation_layout_ids: number[]
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<{ presentation_layout_ids: number[] }, ApiMessageResponse>(
    "POST",
    `${basePath(nationId, departmentId)}/reorder`,
    { presentation_layout_ids }
  );
}
